import type { CSSProperties } from 'react';

// ============================================================
// LumeMark institucional (versão marketing, sem deps de app)
// ============================================================
// SVG inline da marca Lume. Recria o blob navy orgânico + núcleo
// radial quente do logo oficial. Theme-aware via CSS vars
// (--lume-mark-navy-1/2 e --color-warm-*).
// ============================================================

interface LumeMarkProps {
  size?: number;
  className?: string;
  /** Halo radial pulsante (apenas decorativo no hero). */
  animated?: boolean;
  /** Marca acessível com role="img" + <title>. Sem isto é decorativa. */
  title?: string;
}

export function LumeMark({ size = 32, className, animated, title }: LumeMarkProps) {
  const labeled = Boolean(title);
  return (
    <span
      data-testid="lume-mark"
      className={`lume-mark ${animated ? 'lume-mark--animated' : ''} ${className ?? ''}`.trim()}
      style={
        {
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: size,
          height: size,
        } as CSSProperties
      }
      aria-hidden={labeled ? undefined : true}
    >
      <svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        role={labeled ? 'img' : undefined}
        aria-hidden={labeled ? undefined : true}
        style={{ position: 'relative', zIndex: 1, height: '100%', width: '100%' }}
      >
        {labeled ? <title>{title}</title> : null}
        <defs>
          <radialGradient id="lume-warm-mk" cx="42%" cy="40%" r="55%">
            <stop offset="0%" stopColor="#FFE082" />
            <stop offset="35%" stopColor="#FFC857" />
            <stop offset="70%" stopColor="#F08C2E" />
            <stop offset="100%" stopColor="#C9621A" />
          </radialGradient>
          <radialGradient id="lume-edge-mk" cx="42%" cy="40%" r="80%">
            <stop offset="55%" stopColor="rgba(201,98,26,0)" />
            <stop offset="78%" stopColor="rgba(70,40,15,0.18)" />
            <stop offset="100%" stopColor="rgba(70,40,15,0.32)" />
          </radialGradient>
          <radialGradient id="lume-navy-mk" cx="50%" cy="50%" r="60%">
            <stop offset="0%" stopColor="var(--lume-mark-navy-1, #2B5479)" />
            <stop offset="100%" stopColor="var(--lume-mark-navy-2, #173A5C)" />
          </radialGradient>
        </defs>

        <path
          d="M50 4
             C72 4 92 18 96 42
             C100 64 88 84 66 93
             C44 99 18 92 8 72
             C-1 54 4 30 22 16
             C30 9 40 4 50 4 Z"
          fill="url(#lume-navy-mk)"
        />
        <ellipse cx="42" cy="44" rx="26" ry="24" fill="url(#lume-warm-mk)" />
        <ellipse cx="42" cy="44" rx="26" ry="24" fill="url(#lume-edge-mk)" />
      </svg>

      <style>{`
        .lume-mark--animated::before {
          content: "";
          position: absolute;
          inset: -10%;
          border-radius: 9999px;
          background: radial-gradient(
            circle at 42% 40%,
            var(--color-warm-500) 0%,
            var(--color-warm-700) 40%,
            rgba(255, 200, 87, 0) 70%
          );
          filter: blur(8px);
          z-index: 0;
          animation: lume-mark-halo-mk 2.6s cubic-bezier(0.4, 0, 0.6, 1) infinite;
          pointer-events: none;
        }
        @keyframes lume-mark-halo-mk {
          0%   { transform: scale(0.92); opacity: 0.55; }
          60%  { transform: scale(1.20); opacity: 0; }
          100% { transform: scale(1.20); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .lume-mark--animated::before { animation: none; opacity: 0.35; }
        }
      `}</style>
    </span>
  );
}
